import {useEffect, useState, useContext} from 'react';
import {Button, ProgressBar} from 'react-bootstrap';
import {FaHome, FaGraduationCap, FaWhatsapp} from 'react-icons/fa';
import styled from 'styled-components';
import {ReactComponent as BalloonIcon} from 'icons/balloon.svg';
import {ReactComponent as SpyglassIcon} from 'icons/spyglass.svg';
import {ReactComponent as UniversityIcon} from 'icons/university.svg';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
import {ReactComponent as GraduationIcon} from 'icons/graduation.svg';
import {useNavigate} from 'react-router-dom';
import {useIsMobile} from 'utils/general';
import {EnrollmentContext} from 'contexts/EnrollmentContext';
import {roleToDisplayString} from 'contexts/profileUtil';
import {useHistoryNavigate} from 'hooks/router/useHistoryNavigate';
import {useCourseStartDateLabel} from 'hooks/database/useCourseStartDateLabel';
import {useShowDeveloperButtonIfHidden} from 'contexts/DeveloperToolsContext';
import {WelcomeNav} from './WelcomeNav';
import {ProfileProvider} from 'contexts/ProfileContext';
import type {CourseId} from 'types/common';
import LogOnClick from 'engagement/LogOnClick';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {getDoc} from 'firebase/firestore';
import {
  getCourseDocumentRef,
  type CourseDocument,
} from '@cip/core/firebase/client';
import {getAnnouncedRole} from 'course/application/general/utils/decisionAnnouncement';
import {Role} from 'types/role';

interface CourseDescription {
  title: string;
  id: CourseId;
}

/**
 * This is the list of courses that we will display in the welcome page.
 * We will display the courses in this order.
 */
const POSSIBLE_COURSES: CourseDescription[] = [
  {title: 'Code in Place X', id: 'cipx'},
  {title: 'Probability for AI', id: 'pai1'},
  {title: 'Code in Place - 2026', id: 'cip6'},
  {title: 'Code in Place - 2025', id: 'cip5'},
  {title: 'Code in Place - 2024', id: 'cip4'},
  {title: 'Code in Place - 2023', id: 'cip3'},
  {title: 'PYLATINO - 2025', id: 'pylatino-25'},
];

export const WelcomeChoice = () => {
  return (
    <ProfileProvider>
      <WelcomeInner />
    </ProfileProvider>
  );
};

const WelcomeInner = () => {
  const isMobile = useIsMobile();
  const hideFooter = isMobile;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const showFooter = !hideFooter;

  useShowDeveloperButtonIfHidden();

  // Sourced from `course/cipx.startDate` so the copy stays correct across
  // cycles; falls back to date-free copy while loading or if it's unset.
  const cipxStartDateLabel = useCourseStartDateLabel('cipx');

  return (
    <OuterBody>
      <InnerBody>
        <FullContent>
          <WelcomeNav />
          <div style={{flexGrow: 1}} />
          <CenteredContent>
            <EnrolledCourses />
            <YourCoursesTitle>Opportunities</YourCoursesTitle>
            <LogOnClick
              eventName="select_content"
              eventParams={{
                content_type: 'button',
                content_id: 'interest_form_buttom=n',
                selection_context: 'welcome_choice',
              }}
            >
              <ButtonCard
                to="/cipx/intro"
                title="Interested in joining the next live course?"
                text={
                  cipxStartDateLabel
                    ? `A new version of Code in Place starts ${cipxStartDateLabel}.`
                    : 'A new version of Code in Place is coming soon.'
                }
                icon={<BalloonIcon style={{width: '50px', height: '50px'}} />}
              />
            </LogOnClick>
            {/* <LogOnClick
              eventName="select_content"
              eventParams={{
                content_type: 'button',
                content_id: 'apply_to_teach_button',
                selection_context: 'welcome_choice',
              }}
            >
              <ButtonCard
                to="/public/applyteach/cip5"
                title="Apply to Teach for Code in Place 2025"
                text="Give the gift of knowledge to others."
                icon={
                  <GraduationIcon style={{width: '50px', height: '50px'}} />
                }
              />
            </LogOnClick> */}
            <LogOnClick
              eventName="select_content"
              eventParams={{
                content_type: 'button',
                content_id: 'apply_to_teach_button',
                selection_context: 'welcome_choice',
              }}
            >
              <ButtonCard
                to="/public"
                title="Code in Place - Self-Guided Course"
                text="Self-paced course lessons, assignments and more."
                icon={<SpyglassIcon style={{width: '50px', height: '50px'}} />}
              />
            </LogOnClick>
            <LogOnClick
              eventName="select_content"
              eventParams={{
                content_type: 'button',
                content_id: 'whatsapp_learn_button',
                selection_context: 'welcome_choice',
              }}
            >
              <ButtonCard
                to="https://wa.me/16505750929?text=hello%20world"
                isExternalLink={true}
                title="Code in Place - Over WhatsApp"
                text="Self-paced course lessons and assignments all over WhatsApp."
                icon={
                  <FaWhatsapp
                    style={{width: '50px', height: '50px', color: 'lightgreen'}}
                  />
                }
              />
            </LogOnClick>
            {/* <ButtonCard
              to="/public/learn/welcome-to-karel"
              title="Start the first lesson"
              text="The very first step in the coding course."
              icon={<BalloonIcon style={{ width: '50px', height: '50px' }} />}
            /> */}

            <AlumniCenter />

            <div style={{height: 50}} />

            {/* <SLApplicationLink /> */}
          </CenteredContent>
          <div style={{flexGrow: 2}} />
        </FullContent>
      </InnerBody>
      {/* {showFooter && <hr />}
    {showFooter &&
      <Footer>
        <ContinueButton />
      </Footer>
    } */}
    </OuterBody>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const SLApplicationLink = () => {
  const navigate = useHistoryNavigate();
  return (
    <button
      onClick={() => {
        navigate(`/public/applyteach/cip5`);
      }}
      className="btn btn-link"
    >
      Apply to Teach for Code in Place 2025
    </button>
  );
};

/**
 * Reads, once, the course document of every listed course the viewer holds a
 * role in.
 *
 * @param courseIds - The courses to read.
 * @return The documents by course id (undefined for one that is missing or
 *   failed to read), or null until every read has settled.
 * @throws Never; a failed read is logged and reads as undefined.
 */
const useCourseDocs = (
  courseIds: CourseId[],
): Record<CourseId, CourseDocument | undefined> | null => {
  const courseIdsKey = courseIds.join(',');
  // Tagged with the ids it was read for, so a stale result is never handed
  // back for a newer set of courses.
  const [result, setResult] = useState<{
    courseIdsKey: string;
    courseDocs: Record<CourseId, CourseDocument | undefined>;
  } | null>(null);

  useEffect(() => {
    let isCancelled = false;
    Promise.all(
      courseIds.map(async courseId => {
        try {
          const snapshot = await getDoc(getCourseDocumentRef({courseId}));
          return [courseId, snapshot.data()] as const;
        } catch (e) {
          console.debug('[EnrolledCourses] course read failed', courseId, e);
          return [courseId, undefined] as const;
        }
      }),
    ).then(entries => {
      if (!isCancelled) {
        setResult({courseIdsKey, courseDocs: Object.fromEntries(entries)});
      }
    });
    return () => {
      isCancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- courseIdsKey stands in for the array, which is rebuilt every render.
  }, [courseIdsKey]);

  return result?.courseIdsKey === courseIdsKey ? result.courseDocs : null;
};

const EnrolledCourses = () => {
  const {roles} = useContext(EnrollmentContext);
  const t = useTranslateFunction('miscUI');

  const courseDocs = useCourseDocs(
    POSSIBLE_COURSES.filter(({id}) => roles[id]).map(({id}) => id),
  );
  // An unannounced admission must not show, so nothing renders until every
  // course has been checked.
  if (courseDocs === null) {
    return <></>;
  }

  const enrolledCourses: CourseDescription[] = [];
  POSSIBLE_COURSES.forEach(courseDescription => {
    const role = getAnnouncedRole({
      role: roles[courseDescription.id],
      courseData: courseDocs[courseDescription.id],
    });
    if (role === undefined || role === Role.UNREGISTERED) {
      return;
    }
    enrolledCourses.push(courseDescription);
  });

  if (enrolledCourses.length === 0) {
    return <></>;
  }

  // return all the button cards
  const courseHtml = enrolledCourses.map(courseDescription => {
    const roleEnum = roles[courseDescription.id];
    const roleStr = roleToDisplayString(roleEnum);
    return (
      // eslint-disable-next-line @eslint-react/no-missing-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      <LogOnClick
        eventName="select_content"
        eventParams={{
          content_type: 'button',
          content_id: 'apply_to_learn_button',
          selection_context: 'welcome_choice',
        }}
      >
        <ButtonCard
          to={`/${courseDescription.id}`}
          title={courseDescription.title}
          text={`${t('Role')}: ${t(roleStr)}.`}
          icon={<UniversityIcon style={{width: '50px', height: '50px'}} />}
          key={courseDescription.id}
        />
      </LogOnClick>
    );
  });

  return (
    <>
      <YourCoursesTitle>{t('Enrolled Courses')}</YourCoursesTitle>
      {courseHtml}
    </>
  );
};

const AlumniCenter = () => {
  const {roles} = useContext(EnrollmentContext);

  // these are the courses to check. Display in this order
  const isAlum = 'alumni' in roles;
  const roleStr = isAlum ? roleToDisplayString(roles['alumni']) : '';

  if (!isAlum) {
    return <></>;
  }

  return (
    <ButtonCard
      to={`/alumni`}
      title={'Code in Place Alumni Center'}
      text={`Role: ${roleStr}.`}
      icon={<FaGraduationCap style={{width: '50px', height: '50px'}} />}
      key={'alumni'}
    />
  );
};

const ButtonCard = ({
  title,
  text,
  icon,
  to,
  isExternalLink = false,
}: {
  title: string;
  text: string;
  icon: React.ReactNode;
  to: string;
  /** If true, the link points outside of the app. */
  isExternalLink?: boolean;
}) => {
  // on click you should navigate to the href passed as "to"
  const navigate = useHistoryNavigate();
  return (
    <ButtonCardOuter className="card mb-3" onClick={() => {
      if (isExternalLink) {
        window.open(to, '_blank');
      } else {
        navigate(to);
      }
    }}>
      <div className="row g-0">
        <div className="col">
          <ButtonCardInner>
            <div style={{width: 80, display: 'flex', justifyContent: 'center'}}>
              {icon}
            </div>
            <div className="">
              <div className="card-body" style={{paddingLeft: '0px'}}>
                <h5 className="card-title">{title}</h5>
                <p className="card-text">{text}</p>
              </div>
            </div>
          </ButtonCardInner>
        </div>
      </div>
    </ButtonCardOuter>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const WelcomeChoiceProgress = ({}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const [now, setNow] = useState(100); // Initial progress
  const navigate = useNavigate();
  return (
    <WelcomeProgressOuter>
      <button
        aria-label="back button"
        onClick={() => navigate('/')}
        className="btn btn-light mr-1"
      >
        <FaHome />
      </button>
      <ProgressBar now={now} className="flex-grow-1" />
    </WelcomeProgressOuter>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const TopSpeechBubble = ({text}) => (
  <SpeechBubbleContainer style={{minHeight: 80}}>
    <SpeechBubbleText tabIndex={0}>{text}</SpeechBubbleText>
    <TailBottom />
  </SpeechBubbleContainer>
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const RightSpeechBubble = ({text}) => (
  <SpeechBubbleContainer>
    <SpeechBubbleText tabIndex={0}>{text}</SpeechBubbleText>
    <TailLeft />
  </SpeechBubbleContainer>
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const ContinueButton = () => {
  return (
    <Button disabled={true} size="lg">
      Continue
    </Button>
  );
};

const ButtonCardOuter = styled.button`
  max-width: 500px;
  text-align: left;
  width: 100%;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0; /* Change to your desired hover background color */
  }
`;

const ButtonCardInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 5px;
`;

const WelcomeProgressOuter = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const KarelLarge = styled.img`
  max-width: 200px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const KarelSmall = styled.img`
  max-width: 90px;
`;

const CenteredContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const FullContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: left;
  width: 100%;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const Footer = styled.div`
  height: 145px;
  width: 100%;
  border-top: 1px solid #eaeaea;
  display: flex;
  align-items: end;
  flex-direction: column;
  max-width: 1200px;
  justify-content: center;

  @media (max-width: 768px) {
    /* Adjusts the width for mobile devices */
    height: 90px; /* Height for mobile */
    align-items: center; /* Aligns the buttons to the center */
  }
`;

const SpeechBubbleContainer = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 10px;
  padding: 10px 20px;
  margin: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid grey;
`;

const SpeechBubbleText = styled.p`
  margin: 0;
  color: #333333;
  font-size: 16px;
  text-align: center;
`;

const TailBottom = styled.div`
  position: absolute;
  bottom: -10px; // The tip of the tail will align with the bottom of the bubble
  left: 50%;
  transform: translateX(-50%) rotate(225deg);
  background: #ffffff;
  width: 20px; // Width of the tail square before rotation
  height: 20px; // Height of the tail square before rotation
  content: '';
  clip-path: polygon(0 0, 100% 0, 0 100%); // Clip the square to form a triangle
  border: 1px solid grey;
`;

const TailLeft = styled.div`
  position: absolute;
  top: 50%; // Align the center of the tail with the center of the bubble's side
  left: -10px; // The tip of the tail will align with the left side of the bubble
  transform: translateY(-50%) rotate(315deg); // Adjust rotation to point left
  background: #ffffff;
  width: 20px; // Width of the tail square before rotation
  height: 20px; // Height of the tail square before rotation
  content: '';
  clip-path: polygon(0 0, 100% 0, 0 100%); // Clip the square to form a triangle
  border: 1px solid grey;
`;

const OuterBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  padding-top: 0px;
  height: 100vh;

  @media (max-width: 768px) {
    /* Adjusts the width for mobile devices */
    padding: 0px; /* Padding for mobile */
  }
`;

const InnerBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  width: 100%;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const CourseContentHr = styled.hr`
  width: 500px;
  margin: 20px 0;
  margin-top: 10px;
  margin-bottom: 25px;
  border: black solid 1px;
`;

const YourCoursesTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  margin-bottom: 10px;
`;
